import { createSlice } from "@reduxjs/toolkit";
import {
  getCompanies,
  getCompanyById,
  addCompany,
  updateCompany,
  deleteCompany,
  addCompanySignature,
  getEstablishments,
  addEstablishment,
  updateEstablishment,
  deleteEstablishment,
  getEmissionPoints,
  addEmissionPoint,
  updateEmissionPoint,
  deleteEmissionPoint,
} from "./thunk";

interface CompanyState {
  companyList: any[];
  selectedCompany: any | null;
  establishmentList: any[];
  emissionPointList: any[];
  currentSignature: any | null;
  isLoading: boolean;
  error: string | null;
  isSaving: boolean;
}

export const initialState: CompanyState = {
  companyList: [],
  selectedCompany: null,
  establishmentList: [],
  emissionPointList: [],
  currentSignature: null,
  isLoading: false,
  error: null,
  isSaving: false,
};

const companiesSlice = createSlice({
  name: "companies",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Companies
      .addCase(getCompanies.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCompanies.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.companyList = action.payload;
      })
      .addCase(getCompanies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || null;
      })
      .addCase(getCompanyById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCompanyById.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.selectedCompany = action.payload;
        state.establishmentList = action.payload.establishments;
        state.currentSignature = action.payload.signatures?.[0] || null;
      })
      .addCase(getCompanyById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || null;
      })
      .addCase(addCompany.fulfilled, (state, action: any) => {
        state.companyList.push(action.payload);
      })
      .addCase(updateCompany.fulfilled, (state, action: any) => {
        state.companyList = state.companyList.map((company: any) =>
          company.id === action.payload.id ? action.payload : company
        );
      })
      .addCase(deleteCompany.fulfilled, (state, action: any) => {
        state.companyList = state.companyList.filter(
          (company: any) => company.id !== action.payload
        );
      })

      // Signature
      .addCase(addCompanySignature.fulfilled, (state, action: any) => {
        state.currentSignature = action.payload;
      })
      
      // Establishments
      .addCase(getEstablishments.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getEstablishments.fulfilled, (state, action: any) => {
        state.isLoading = false;
        // Normalizar: asegurar siempre un arreglo, sin importar el envoltorio
        const payload = action.payload;
        const toArray = (p: any): any[] => {
          if (Array.isArray(p)) return p;
          if (Array.isArray(p?.data)) return p.data;
          if (Array.isArray(p?.items)) return p.items;
          if (Array.isArray(p?.data?.items)) return p.data.items;
          if (Array.isArray(p?.results)) return p.results;
          if (p && typeof p === 'object' && (p.id || p._id)) return [p];
          return [];
        };
        state.establishmentList = toArray(payload);
      })
      .addCase(getEstablishments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || null;
      })
      .addCase(addEstablishment.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(addEstablishment.fulfilled, (state, action: any) => {
        state.isSaving = false;
        state.establishmentList.push(action.payload);
      })
      .addCase(addEstablishment.rejected, (state) => {
        state.isSaving = false;
      })
      .addCase(updateEstablishment.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(updateEstablishment.fulfilled, (state, action: any) => {
        state.isSaving = false;
        state.establishmentList = state.establishmentList.map((item: any) =>
          item.id === action.payload.id ? action.payload : item
        );
      })
      .addCase(updateEstablishment.rejected, (state) => {
        state.isSaving = false;
      })
      .addCase(deleteEstablishment.fulfilled, (state, action: any) => {
        state.establishmentList = state.establishmentList.filter(
          (item: any) => item.id !== action.payload
        );
      })

      // Emission Points
      .addCase(getEmissionPoints.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getEmissionPoints.fulfilled, (state, action: any) => {
        state.isLoading = false;
        // Normalizar: asegurar siempre un arreglo, sin importar el envoltorio
        const payload = action.payload;
        const toArray = (p: any): any[] => {
          if (Array.isArray(p)) return p;
          if (Array.isArray(p?.data)) return p.data;
          if (Array.isArray(p?.items)) return p.items;
          if (Array.isArray(p?.data?.items)) return p.data.items;
          if (Array.isArray(p?.results)) return p.results;
          return [];
        };
        state.emissionPointList = toArray(payload);
      })
      .addCase(getEmissionPoints.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || null;
      })
      .addCase(addEmissionPoint.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(addEmissionPoint.fulfilled, (state, action: any) => {
        state.isSaving = false;
        state.emissionPointList.push(action.payload);
      })
      .addCase(addEmissionPoint.rejected, (state) => {
        state.isSaving = false;
      })
      .addCase(updateEmissionPoint.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(updateEmissionPoint.fulfilled, (state, action: any) => {
        state.isSaving = false;
        state.emissionPointList = state.emissionPointList.map((item: any) =>
          item.id === action.payload.id ? action.payload : item
        );
      })
      .addCase(updateEmissionPoint.rejected, (state) => {
        state.isSaving = false;
      })
      .addCase(deleteEmissionPoint.fulfilled, (state, action: any) => {
        state.emissionPointList = state.emissionPointList.filter(
          (item: any) => item.id !== action.payload
        );
      });
  },
});

export default companiesSlice.reducer;
